import { Modal } from 'theme';

class NewsletterCapture extends Modal {
  connectedCallback() {
    super.connectedCallback();
    this.trigger = this.controls[0];
    this.triggerReady = false;
    this.triggerEntered = false;
    this.mobile = window.matchMedia('(max-width: 699px)');
    this.filled = this.dataset.success === 'true' || this.readMarker('theme:popup-filled');
    this.appeared = this.hasAttribute('only-once') && this.readMarker('theme:popup-appeared');
    this.delayPassed = false;
    this.remainingTime = Number(this.getAttribute('apparition-delay')) * 1000;
    const options = { signal: this.abortController.signal };
    const update = this.update.bind(this);

    if (this.dataset.success === 'true') this.remember('theme:popup-filled');
    this.addEventListener('dialog:before-show', () => {
      this.appeared = true;
      this.remember('theme:popup-appeared');
    }, options);
    this.addEventListener('dialog:after-show', () => this.querySelector('h2').focus({ preventScroll: true }), options);
    this.addEventListener('dialog:after-hide', update, options);
    document.addEventListener('toggle', update, options);
    document.addEventListener('focusin', update, options);
    document.addEventListener('focusout', () => setTimeout(update, 0), options);
    document.addEventListener('visibilitychange', () => {
      this.scheduleDelay();
      this.update();
    }, options);
    window.addEventListener('scroll', update, { ...options, passive: true });
    window.addEventListener('resize', update, options);
    window.visualViewport?.addEventListener('resize', update, options);
    window.addEventListener('storage', (event) => {
      if (event.key === 'theme:popup-filled' && event.newValue === 'true') {
        this.filled = true;
        this.hide();
      }
      if (event.key === 'theme:popup-appeared' && event.newValue === 'true') this.appeared = true;
      this.update();
    }, options);
    this.update();
    this.scheduleDelay();
    const scheduleTrigger = () => {
      this.triggerTimer = setTimeout(() => {
        this.triggerReady = true;
        this.update();
      }, 1000);
    };
    // Wait for the page load so its entrance is visible after the content settles.
    if (document.readyState === 'complete') scheduleTrigger();
    else window.addEventListener('load', scheduleTrigger, { ...options, once: true });
  }

  disconnectedCallback() {
    clearTimeout(this.delayTimer);
    clearTimeout(this.triggerTimer);
    super.disconnectedCallback();
  }

  get shouldAppendToBody() {
    return false;
  }

  readMarker(key) {
    try {
      return localStorage.getItem(key) === 'true';
    } catch {
      return false;
    }
  }

  remember(key) {
    try {
      localStorage.setItem(key, 'true');
    } catch {
      // Private browser settings can block storage; this visit still remembers the state.
    }
  }

  scheduleDelay() {
    clearTimeout(this.delayTimer);
    if (this.timerStarted !== undefined) {
      this.remainingTime -= performance.now() - this.timerStarted;
      this.timerStarted = undefined;
    }
    if (document.hidden || this.delayPassed || Shopify.designMode || this.dataset.homepage !== 'true') return;
    this.timerStarted = performance.now();
    this.delayTimer = setTimeout(() => {
      this.timerStarted = undefined;
      this.delayPassed = true;
      this.update();
    }, Math.max(0, this.remainingTime));
  }

  update() {
    if (!this.isConnected) return;
    const focusedElement = document.activeElement;
    // Keep the return-focus control available as the modal closes.
    const inputActive = focusedElement?.matches('input, textarea, select, [contenteditable="true"]') && !this.contains(focusedElement);
    const otherDialogOpen = [...document.querySelectorAll('[role="dialog"][open], dialog[open]')].some(dialog => dialog !== this);
    const keyboardOpen = window.visualViewport && window.visualViewport.height < window.innerHeight * .75;
    this.trigger.hidden = !this.triggerReady || this.open || this.filled || inputActive || otherDialogOpen || keyboardOpen || document.hidden;

    if (!this.trigger.hidden && !this.triggerEntered) {
      this.triggerEntered = true;
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.trigger.animate([
          { transform: 'translateX(calc(100% + 24px))' },
          { transform: 'translateX(0)' }
        ], { duration: 450, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
      }
    }

    if (this.trigger.hidden || Shopify.designMode || !this.delayPassed || this.dataset.homepage !== 'true') return;
    if (this.appeared) return;
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (this.mobile.matches && (scrollableHeight <= 0 || window.scrollY / scrollableHeight < .4)) return;
    this.show();
  }

  createEnterAnimationControls() {
    const controls = super.createEnterAnimationControls();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) controls.finish();
    return controls;
  }

  createLeaveAnimationControls() {
    const controls = super.createLeaveAnimationControls();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) controls.finish();
    return controls;
  }
}

if (!customElements.get('dk-newsletter-popup')) customElements.define('dk-newsletter-popup', NewsletterCapture);
